import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Result, ConfigProvider } from "antd";
import faIR from "antd/locale/fa_IR";
import MainLayout from "./components/MainLayout";
import EmployeesPage from "./pages/EmployeesPage";
import AttendancePage from "./pages/AttendancePage";
import InsurancePage from "./pages/InsurancePage";
import PayrollPage from "./pages/PayrollPage";
import PieceRatePage from "./pages/PieceRatePage";
import DashboardPage from "./pages/DashboardPage";
import DepartmentsPage from "./pages/DepartmentsPage";
import EmployeeImportPage from "./pages/EmployeeImportPage";
import FinancialLedgerPage from "./pages/FinancialLedgerPage";
import InsuranceDriverPage from "./pages/InsuranceDriverPage";
import ReportsPage from "./pages/ReportsPage";
import SettingsPage from "./pages/SettingsPage";
import ControlCenterPage from "./pages/ControlCenterPage";
import GuidePage from "./pages/GuidePage";
import AssetsPage from "./pages/AssetsPage";
import DyePage from "./pages/dye/DyePage";
// ماژول زنجیره تأمین
import LoginPage from "./pages/LoginPage";
import SupplyChainDashboard from "./pages/supplychain/SupplyChainDashboard";
import MovementEventsPage from "./pages/supplychain/MovementEventsPage";
import OpeningBalancePage from "./pages/supplychain/OpeningBalancePage";
import PartiesPage from "./pages/supplychain/PartiesPage";
import CancelledEventsPage from "./pages/supplychain/CancelledEventsPage";
import CustodyLedgerPage from "./pages/supplychain/CustodyLedgerPage";
import BatchTrackingPage from "./pages/supplychain/BatchTrackingPage";
import WarehouseReceiptPage from "./pages/supplychain/WarehouseReceiptPage";
import SupplyChainReports from "./pages/supplychain/SupplyChainReports";
import ImportCenterPage from "./pages/supplychain/ImportCenterPage";
import ScaleDiscrepancyPage from "./pages/supplychain/ScaleDiscrepancyPage";
import FreightPage from "./pages/supplychain/FreightPage";
import InvoiceCreatePage from "./pages/supplychain/InvoiceCreatePage";
import InvoiceWorkspace from "./pages/supplychain/InvoiceWorkspace";
import CutRegisterPage from "./pages/production/CutRegisterPage";
import WasteRegisterPage from "./pages/production/WasteRegisterPage";
import ProductionManagePage from "./pages/production/ProductionManagePage";
import NotesPage from "./pages/NotesPage";
import ChatPage from "./pages/ChatPage";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider, useThemeMode } from "./theme/ThemeContext";
import { NavigationGuardProvider } from "./hooks/useNavigationGuard";
import { getTheme } from "./theme/theme";
import "./theme/gharchak-theme.css";
import { homeFor } from "./utils/homePages";

// نگهبانِ ماژول زنجیره تأمین — اگر وارد نشده باشد صفحهٔ ورود نشان داده می‌شود
function RequireAuth({ children }) {
  const { isAuthed } = useAuth();
  return isAuthed ? children : <LoginPage />;
}

/**
 * گیتِ مجوز روی خودِ مسیر — پنهان‌کردنِ آیتمِ منو جلوی تایپِ مستقیمِ آدرس را نمی‌گیرد.
 * `anyOf`: داشتنِ حداقل یکی از مجوزها کافی است.
 */
function RequirePerm({ anyOf, children }) {
  const { isAuthed, can, user } = useAuth();
  const location = useLocation();
  if (!isAuthed) return <LoginPage />;
  const list = Array.isArray(anyOf) ? anyOf : [anyOf];
  if (list.some((p) => can(p))) return children;
  // v2.169.0 — به‌جای «دسترسی ندارید» به صفحهٔ اولِ خودش برود (کاربر: «آنجا نزند دسترسی ندارید»)
  const home = homeFor(user, can);
  if (home.split("?")[0] !== location.pathname) return <Navigate to={home} replace />;
  return (
    <Result status="403" title="دسترسی ندارید"
      subTitle="این بخش برای نقشِ شما فعال نیست. در صورت نیاز با مدیر سیستم هماهنگ کنید." />
  );
}

/** صفحهٔ اول: انتخابِ کاربر در «تنظیمات ← کاربران»، وگرنه اولین صفحه‌ای که دسترسی دارد (`utils/homePages`). */
function HomeRedirect() {
  const { can, user } = useAuth();
  return <Navigate to={homeFor(user, can)} replace />;
}

function InvoiceAliasRedirect() {
  const { id } = useParams();
  return <Navigate to={`/supply-chain/events/${id}`} replace />;
}

function ThemedApp() {
  const { mode, palette } = useThemeMode();
  return (
    <ConfigProvider locale={faIR} direction="rtl" theme={getTheme(mode, palette)}>
      <BrowserRouter>
        <NavigationGuardProvider>
        <Routes>
          {/* کلِ اپ پشتِ ورود — بدونِ لاگین هیچ صفحه‌ای باز نمی‌شود (امنیتِ سرورِ عمومی) */}
          <Route path="/" element={<RequireAuth><MainLayout /></RequireAuth>}>
            <Route index element={<HomeRedirect />} />
            <Route path="dashboard" element={<RequirePerm anyOf="dashboard.view"><DashboardPage /></RequirePerm>} />
            <Route path="departments" element={<DepartmentsPage />} />
            <Route path="employees" element={<RequirePerm anyOf="personnel.view"><EmployeesPage /></RequirePerm>} />
            <Route path="employee-import" element={<EmployeeImportPage />} />
            <Route path="attendance" element={<RequirePerm anyOf="attendance.view"><AttendancePage /></RequirePerm>} />
            <Route path="piece-rate" element={<RequirePerm anyOf={["production.rates.manage", "production.rates.create", "production.view_amounts", "amounts.view_production", "amounts.view_all"]}><PieceRatePage /></RequirePerm>} />
            {/* زنجیرهٔ تولید: ثبت برش → ثبت ضایعات → ثبت و مدیریت تولید (کنترل اول/نهایی داخلش) */}
            <Route path="production/cut" element={<CutRegisterPage />} />
            <Route path="production/waste" element={<WasteRegisterPage />} />
            <Route path="production" element={<ProductionManagePage />} />
            <Route path="production/:code" element={<ProductionManagePage />} />
            {/* مسیرهای قدیمیِ پارت/بیجک/کنترل/سری‌ها — همه در «ثبت و مدیریت تولید» ادغام شدند */}
            <Route path="production/parts" element={<Navigate to="/production" replace />} />
            <Route path="production/part/:id" element={<Navigate to="/production" replace />} />
            <Route path="production/part/:id/:stage" element={<Navigate to="/production" replace />} />
            <Route path="production/qc" element={<Navigate to="/production" replace />} />
            <Route path="orders" element={<Navigate to="/production" replace />} />
            <Route path="payroll" element={<RequirePerm anyOf="payroll.view"><PayrollPage /></RequirePerm>} />
            <Route path="finance" element={<RequirePerm anyOf="accounting.view"><FinancialLedgerPage /></RequirePerm>} />
            {/* کسورات/مساعده و بیمه/راننده داخل دفتر مالی یکپارچه شدند */}
            <Route path="deductions" element={<Navigate to="/finance?tab=ledger" replace />} />
            <Route path="insurance" element={<RequirePerm anyOf="accounting.view"><InsurancePage /></RequirePerm>} />
            {/* 🐞 این مسیر به بیمه ریدایرکت می‌شد و صفحهٔ راننده‌ها **دسترسی‌ناپذیر**
                بود — کامپوننتش ساخته شده بود ولی هیچ‌جا import نمی‌شد. */}
            <Route path="drivers" element={<RequirePerm anyOf="payroll.view"><InsuranceDriverPage /></RequirePerm>} />
            <Route path="insurance-driver" element={<Navigate to="/drivers" replace />} />
            {/* اموال — دسترسیِ انحصاری؛ هیچ نقشی (حتی مدیرِ اصلی) بدونِ تیکِ صریح نمی‌بیند */}
            <Route path="assets" element={<RequirePerm anyOf="assets.view"><AssetsPage /></RequirePerm>} />
            {/* پیگیری رنگرزی — دسترسیِ انحصاری (dye.*)؛ گوشی‌محور */}
            <Route path="dye" element={<RequirePerm anyOf="dye.view"><DyePage /></RequirePerm>} />
            <Route path="reports" element={<RequirePerm anyOf="reports.view"><ReportsPage /></RequirePerm>} />
            <Route path="notes" element={<Navigate to="/chat?tab=notes" replace />} />
            <Route path="chat" element={<ChatPage />} />
            {/* صفحهٔ بستن حقوق با صفحهٔ محاسبه ادغام شد؛ مسیر قدیمی ریدایرکت می‌شود */}
            <Route path="payroll-closing" element={<Navigate to="/payroll" replace />} />
            <Route path="settings" element={<RequirePerm anyOf="settings.view"><SettingsPage /></RequirePerm>} />
            <Route path="control-center" element={<ControlCenterPage />} />
            <Route path="guide" element={<GuidePage />} />
            {/* زنجیره تأمین — پشتِ ورود **و** پشتِ مجوز.
                🔴 قبلاً فقط `RequireAuth` بود: کاربری بدونِ `supply_chain.view`
                صفحه را می‌دید، ولی هر درخواستِ داده ۴۰۳ می‌گرفت و کاربر یک
                «خطا + تلاش مجدد» می‌دید که هرگز درست نمی‌شد — چون مشکل
                گذرا نبود، دسترسی بود. حالا صریح می‌گوید دسترسی ندارید. */}
            <Route path="supply-chain" element={<RequirePerm anyOf="supply_chain.view"><SupplyChainDashboard /></RequirePerm>} />
            <Route path="supply-chain/new" element={<RequirePerm anyOf="supply_chain.view"><InvoiceCreatePage /></RequirePerm>} />
            <Route path="supply-chain/events/:eventId" element={<RequirePerm anyOf="supply_chain.view"><InvoiceWorkspace /></RequirePerm>} />
            <Route path="supply-chain/events" element={<RequirePerm anyOf="supply_chain.view"><MovementEventsPage /></RequirePerm>} />
            <Route path="supply-chain/invoices/:id" element={<InvoiceAliasRedirect />} />
            <Route path="supply-chain/invoices" element={<Navigate to="/supply-chain/events" replace />} />
            <Route path="supply-chain/custody" element={<RequirePerm anyOf="supply_chain.view"><CustodyLedgerPage /></RequirePerm>} />
            <Route path="supply-chain/opening-balance" element={<RequirePerm anyOf="supply_chain.view"><OpeningBalancePage /></RequirePerm>} />
            <Route path="supply-chain/parties" element={<RequirePerm anyOf="supply_chain.view"><PartiesPage /></RequirePerm>} />
            <Route path="supply-chain/cancelled" element={<RequirePerm anyOf="supply_chain.view"><CancelledEventsPage /></RequirePerm>} />
            <Route path="supply-chain/batches" element={<RequirePerm anyOf="supply_chain.view"><BatchTrackingPage /></RequirePerm>} />
            <Route path="supply-chain/warehouse" element={<RequirePerm anyOf="supply_chain.view"><WarehouseReceiptPage /></RequirePerm>} />
            <Route path="supply-chain/reports" element={<RequirePerm anyOf="supply_chain.view"><SupplyChainReports /></RequirePerm>} />
            <Route path="supply-chain/import" element={<RequirePerm anyOf="supply_chain.view"><ImportCenterPage /></RequirePerm>} />
            <Route path="supply-chain/scale-discrepancy" element={<RequirePerm anyOf="supply_chain.view"><ScaleDiscrepancyPage /></RequirePerm>} />
            <Route path="supply-chain/freight" element={<RequirePerm anyOf="supply_chain.view"><FreightPage /></RequirePerm>} />
          </Route>
        </Routes>
        </NavigationGuardProvider>
      </BrowserRouter>
    </ConfigProvider>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ThemedApp />
      </AuthProvider>
    </ThemeProvider>
  );
}
